import { If } from "components";
import { useBoolean } from "hooks";
import filter from "lodash/filter";
import isEmpty from "lodash/isEmpty";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { ClipLoader } from "react-spinners";
import { Modal } from "ui";

import {
  useCreateSubChildCategory,
  useGetCategoryById,
  useUpdateSubChildCategory
} from "../../../apis";
import { useCategoriesNavigation } from "../../../hooks";
import { CategoryModalProps } from "../../components.types";
import { SubChildCategoryForm, SubChildCategoryFormData } from "../../forms";
import { ModalFooter } from "../atoms";

import styles from "./SubChildCategoryModal.module.scss";

const SUB_CHILD_CATEGORY_FORM_ID = "sub-child-category-form-id";

const SubChildCategoryModal = ({
  isOpen,
  category,
  renderDeleteCategory,
  refetchCategories,
  onClose
}: CategoryModalProps) => {
  const { t } = useTranslation();
  const isUpdateCategory = !isEmpty(category);

  const {
    value: isConfirmationModalOpen,
    setTrue: openConfirmationModal,
    setFalse: closeConfirmationModal
  } = useBoolean(false);

  const [confirmatingFormData, setConfirmatingFormsData] =
    useState<SubChildCategoryFormData | null>(null);

  const { subCategoryId } = useCategoriesNavigation();

  const onSuccessActionCategory = () => {
    refetchCategories();
    onClose();
  };

  const { category: editableCategory, isFetching: isEditableCategoryFetching } =
    useGetCategoryById(category?.id);

  const {
    isLoading: isLoadingCreateSubChildCategory,
    mutate: mutateCreateSubChildCategory
  } = useCreateSubChildCategory({
    onSuccess: onSuccessActionCategory
  });

  const {
    isLoading: isLoadingUpdateSubChildCategory,
    mutate: mutateUpdateSubChildCategory
  } = useUpdateSubChildCategory({
    onSuccess: onSuccessActionCategory
  });

  const onSubmit = (formData: SubChildCategoryFormData) => {
    if (filter(formData.attributes, { isMain: true }).length < 2) {
      return toast.error("Should contain two attributes with main filter");
    }

    const mutateCreateOrUpdateSubChildCategory = isUpdateCategory
      ? mutateUpdateSubChildCategory
      : mutateCreateSubChildCategory;

    mutateCreateOrUpdateSubChildCategory({
      ...formData,
      parentCategoryId: subCategoryId,
      id: category?.id,
      imageId: formData?.image?.id
    } as any);
    // TODO: update any
  };

  const checkSubmitConfirmationNecessity = (
    formData: SubChildCategoryFormData
  ) => {
    const hasAdditionalAttributes = formData.attributes.some(
      (attribute) => attribute.type === "Additional" && !attribute.delete
    );

    if (hasAdditionalAttributes) {
      onSubmit(formData);
    } else {
      setConfirmatingFormsData(formData);
      openConfirmationModal();
    }
  };

  const onConfirmSavingClick = () => {
    if (confirmatingFormData) {
      setConfirmatingFormsData(null);
      closeConfirmationModal();
      onSubmit(confirmatingFormData);
    }
  };

  return (
    <Modal
      className={styles["sub-child-category-modal"]}
      contentClassName={styles["sub-child-category-modal_content"]}
      formId={SUB_CHILD_CATEGORY_FORM_ID}
      isOpen={isOpen}
      title={t(`${isUpdateCategory ? "Edit" : "New"} Sub-sub-category`)}
      isLoading={
        isLoadingCreateSubChildCategory || isLoadingUpdateSubChildCategory
      }
      okText={isUpdateCategory ? t("Save") : t("Create")}
      extraFooter={ModalFooter({
        renderDeleteCategory,
        isEditableCategoryFetching,
        editableCategory
      })}
      onClose={onClose}
    >
      <If condition={isUpdateCategory && !editableCategory}>
        <div className={styles["sub-child-category-modal_loader"]}>
          <ClipLoader size={30} color="#fb3973" />
        </div>
      </If>

      <If condition={!isUpdateCategory || editableCategory}>
        <SubChildCategoryForm
          formId={SUB_CHILD_CATEGORY_FORM_ID}
          category={editableCategory ?? null}
          onSubmit={checkSubmitConfirmationNecessity}
        />
      </If>

      <Modal
        type="primary"
        isOpen={isConfirmationModalOpen}
        title={t("Confirm Sub-sub-category saving")}
        className={styles["confirm-modal"]}
        okText="Save"
        onOk={onConfirmSavingClick}
        onClose={onClose}
      >
        <p className={styles["confirm-modal__text"]}>
          {t(
            "You've not added any additional attributes, do you want to continue?"
          )}
        </p>
      </Modal>
    </Modal>
  );
};

export default SubChildCategoryModal;
